import { useState } from 'react';
import { FormField, Input } from '../../../components/ui/FormField.js';
import { api } from '../../../services/api.js';
import { rm } from '../../penalties/penalties.js';
import { LastChanged } from '../parts/LastChanged.js';
import { SettingCard } from '../parts/parts.js';
import { PENALTY_COMMISSION_DEFAULT_BPS } from '../settingsTypes.js';
import type { CardProps } from './cardProps.js';

/**
 * 393 (#1313, FR-003, mock-up screen 6): CARii's payment QR, shown to every customer with an unpaid penalty. The picture
 * is a PRIVATE upload (`penalty_qr`, settings managers only); its id is saved with the tab like every card, and the
 * saved QR is shown through a 10-minute link.
 * 409 (#1471, mock-up frame 1): the card is "Penalties" and also holds CARii's commission on every penalty (one rate for
 * every supplier and car), edited in percent and saved in basis points with the same save.
 */
/**
 * 409 (US3-2): a percent as typed, in basis points. Two decimals become a whole number (12.34 % → 1234, without the
 * float's 1233.9999…); anything finer is sent as typed, and an empty field as nothing, so the platform refuses them
 * with its message instead of the page rounding the rate or saving 0 %.
 */
function toBps(typed: string): number | null {
  if (typed.trim() === '') return null;
  const raw = Number(typed) * 100;
  const rounded = Math.round(raw);
  return Math.abs(raw - rounded) < 1e-6 ? rounded : raw;
}

export function PenaltyQrCard({ draft, config, latest, refusal }: CardProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const value = draft.value('penalty_payment') ?? {
    qr_picture_id: null,
    commission_rate_bps: PENALTY_COMMISSION_DEFAULT_BPS,
  };
  const rateBps = value.commission_rate_bps;
  const whole = rateBps !== null && Number.isInteger(rateBps) && rateBps >= 0 && rateBps <= 10000;
  // The example is a RM 100.00 fine, so CARii's share in sen is the rate in basis points exactly.
  const example = whole
    ? `RM 100.00 fine: CARii ${rm(rateBps)}, payout ${rm(10000 - rateBps)}`
    : 'Enter a rate from 0 to 100 %, with at most two decimals.';
  const savedId = config.penalty_payment?.qr_picture_id ?? null;
  const savedUrl = config.penalty_payment?.qr_url ?? null;
  const replaced = value.qr_picture_id !== savedId;

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      form.append('purpose', 'penalty_qr');
      const res = (await api.upload('/files/upload', form)) as { id: string };
      draft.set('penalty_payment', { ...value, qr_picture_id: res.id });
    } catch (err) {
      setUploadError((err as { message?: string }).message ?? 'The picture could not be uploaded.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  return (
    <SettingCard
      testId="penalty-qr-card"
      title="Penalties"
      dirty={draft.isDirty('penalty_payment')}
      refusal={refusal('penalty_payment')}
      description={
        <p>The payment QR customers scan to pay a penalty, and CARii's share of every fine.</p>
      }
      footer={<LastChanged setting="penalty_payment" latest={latest} />}
    >
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex h-32 w-32 items-center justify-center rounded-lg border border-hairline bg-surface text-xs text-steel">
          {savedUrl && !replaced ? (
            <img
              src={savedUrl}
              alt="Penalty payment QR"
              className="h-full w-full rounded-lg object-contain"
            />
          ) : replaced ? (
            'New QR'
          ) : (
            'No QR yet'
          )}
        </div>
        <div className="flex flex-col gap-2 text-sm">
          <label className="inline-flex w-fit cursor-pointer rounded-full bg-primary px-4 py-2 font-semibold text-on-primary">
            {uploading ? 'Uploading…' : 'Replace image'}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              aria-label="Replace image"
              className="sr-only"
              disabled={uploading}
              onChange={onFile}
            />
          </label>
          <span className="text-xs text-steel">PNG, JPEG or WebP. Shown as uploaded.</span>
          {replaced && <span className="text-xs text-ink">New QR ready: save to use it.</span>}
          {uploadError && (
            <span role="alert" className="text-xs text-semantic-danger-text">
              {uploadError}
            </span>
          )}
        </div>
      </div>
      <div className="mt-4 border-t border-hairline pt-4">
        <p className="text-sm font-semibold text-ink">CARii commission on penalties</p>
        <p className="mb-2 text-xs text-steel">
          CARii keeps this share of every fine; the supplier is paid out the rest. A change applies
          to every penalty not yet paid out.
        </p>
        <FormField label="CARii commission (%)" name="penalty_commission_rate">
          <Input
            id="penalty_commission_rate"
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={
              rateBps === null ? '' : whole ? (rateBps / 100).toFixed(2) : String(rateBps / 100)
            }
            onChange={(e) =>
              draft.set('penalty_payment', {
                ...value,
                commission_rate_bps: toBps(e.target.value),
              })
            }
          />
        </FormField>
        <p data-testid="penalty-commission-example" className="mt-1 text-xs text-steel">
          {example}
        </p>
      </div>
    </SettingCard>
  );
}
